import { useState, useEffect } from 'react';
import '@/App.css';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { translations } from './translations';
import { Globe, Database, LineChart, Map, Menu, X, Linkedin, Mail, LayoutDashboard, ShieldCheck, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const solutionIcons = [
  { icon: Database, color: '#0A2540' },
  { icon: Globe, color: '#FF6B00' },
  { icon: LineChart, color: '#0A2540' },
  { icon: Map, color: '#FF6B00' },
  { icon: LayoutDashboard, color: '#0A2540' },
  { icon: ShieldCheck, color: '#FF6B00' }
];

function App() {
  const [language, setLanguage] = useState('en');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const t = translations[language];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_13933342-c6ae-4121-81b7-77a0c7a9e505/artifacts/nmldjtvn_image.png" 
                alt="Elixir.AI Logo" 
                className="h-12 w-auto"
                data-testid="logo-image"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => scrollToSection('home')} 
                className="nav-link"
                data-testid="nav-home"
              >
                {t.nav.home}
              </button>
              <button 
                onClick={() => scrollToSection('about')} 
                className="nav-link"
                data-testid="nav-about"
              >
                {t.nav.about}
              </button>
              <button 
                onClick={() => scrollToSection('leaders')} 
                className="nav-link"
                data-testid="nav-leaders"
              >
                {t.nav.leaders}
              </button>
              <button 
                onClick={() => scrollToSection('contact')} 
                className="nav-link"
                data-testid="nav-contact"
              >
                {t.nav.contact}
              </button>
            </div>

            {/* Language Switcher & Mobile Menu */}
            <div className="flex items-center space-x-4">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="language-selector"
                data-testid="language-selector"
              >
                <option value="en">EN</option>
                <option value="de">DE</option>
              </select>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2"
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-2" data-testid="mobile-menu">
              <button 
                onClick={() => scrollToSection('home')} 
                className="block w-full text-left px-4 py-2 nav-link"
              >
                {t.nav.home}
              </button>
              <button 
                onClick={() => scrollToSection('about')} 
                className="block w-full text-left px-4 py-2 nav-link"
              >
                {t.nav.about}
              </button>
              <button 
                onClick={() => scrollToSection('leaders')} 
                className="block w-full text-left px-4 py-2 nav-link"
              >
                {t.nav.leaders}
              </button>
              <button 
                onClick={() => scrollToSection('contact')} 
                className="block w-full text-left px-4 py-2 nav-link"
              >
                {t.nav.contact}
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-overlay" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl">
            <div className="hero-slogan" data-testid="hero-slogan">
              {t.hero.slogan}
            </div>
            <h1 className="hero-title" data-testid="hero-title">
              {t.hero.title}<br />
              <span className="hero-accent">{t.hero.titleAccent}</span>
            </h1>
            <p className="hero-subtitle" data-testid="hero-subtitle">
              {t.hero.subtitle}
            </p>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="hero-cta"
              data-testid="hero-cta-button"
            >
              {t.hero.cta}
            </Button>
          </div>
        </div>
      </section>

      {/* About Us - Solutions Section */}
      <section id="about" className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="section-title" data-testid="solutions-title">{t.about.title}</h2>
            <p className="section-subtitle" data-testid="solutions-subtitle">{t.about.subtitle}</p>
          </div>

          <div className="bento-grid">
            {t.about.solutions.map((solution, index) => {
              const IconComponent = solutionIcons[index].icon;
              return (
                <div 
                  key={index} 
                  className="solution-card"
                  data-testid={`solution-card-${index}`}
                >
                  <div 
                    className="solution-icon-wrapper"
                    style={{ backgroundColor: `${solutionIcons[index].color}10` }}
                  >
                    <IconComponent 
                      size={32} 
                      color={solutionIcons[index].color}
                    />
                  </div>
                  <h3 className="solution-title">{solution.title}</h3>
                  <p className="solution-description">{solution.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leaders Section */}
      <section id="leaders" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="section-title" data-testid="leaders-title">{t.leaders.title}</h2>
            <p className="section-subtitle" data-testid="leaders-subtitle">{t.leaders.subtitle}</p>
          </div>

          <div className="leaders-grid">
            {/* CEO Card */}
            <div className="leader-card" data-testid="leader-card-ceo">
              <div className="leader-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_elixir-business-ai/artifacts/gfwh3vl6_Mayurakshi%20Das.jpeg"
                  alt={t.leaders.ceo.name}
                  className="leader-image"
                />
              </div>
              <div className="leader-content">
                <h3 className="leader-name">{t.leaders.ceo.name}</h3>
                <p className="leader-title">{t.leaders.ceo.title}</p>
                <p className="leader-bio">{t.leaders.ceo.bio}</p>
              </div>
            </div>

            {/* CTO Card */}
            <div className="leader-card" data-testid="leader-card-cto">
              <div className="leader-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_elixir-business-ai/artifacts/t0okendl_Vijay%20Solanky.jpeg"
                  alt={t.leaders.cto.name}
                  className="leader-image"
                />
              </div>
              <div className="leader-content">
                <h3 className="leader-name">{t.leaders.cto.name}</h3>
                <p className="leader-title">{t.leaders.cto.title}</p>
                <p className="leader-bio">{t.leaders.cto.bio}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="section-title" data-testid="contact-title">{t.contact.title}</h2>
            <p className="section-subtitle" data-testid="contact-subtitle">{t.contact.subtitle}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 mb-12">
            {/* Contact Information Card */}
            <div className="contact-info-card" data-testid="contact-info-card">
              <h3 className="contact-info-title">{t.contact.getInTouch}</h3>
              
              {/* Phone */}
              <div className="contact-item" data-testid="contact-phone">
                <div className="contact-icon-wrapper" style={{ backgroundColor: '#FF6B0010' }}>
                  <Phone size={24} color="#FF6B00" />
                </div>
                <div className="contact-emails-wrapper">
                  <p className="contact-label">Phone</p>
                  <div className="emails-list">
                    {t.contact.phones.map((phone, index) => (
                      <a 
                        key={index}
                        href={`tel:${phone.replace(/\s/g, '')}`} 
                        className="contact-value"
                        data-testid={`contact-phone-${index}`}
                      >
                        {phone}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Emails */}
              <div className="contact-item" data-testid="contact-emails">
                <div className="contact-icon-wrapper" style={{ backgroundColor: '#0A254010' }}>
                  <Mail size={24} color="#0A2540" />
                </div>
                <div className="contact-emails-wrapper">
                  <p className="contact-label">Email</p>
                  <div className="emails-list">
                    {t.contact.emails.map((contact, index) => (
                      <div key={index} className="email-contact-item" data-testid={`contact-email-${index}`}>
                        <p className="contact-name">{contact.name}</p>
                        <a 
                          href={`mailto:${contact.email}`} 
                          className="contact-value"
                        >
                          {contact.email}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Office Locations Card */}
            <div className="contact-info-card" data-testid="office-locations-card">
              <h3 className="contact-info-title">{t.contact.offices}</h3>
              
              {/* India Office */}
              <div className="contact-item" data-testid="office-card-india">
                <div className="contact-icon-wrapper" style={{ backgroundColor: '#FF6B0010' }}>
                  <MapPin size={24} color="#FF6B00" />
                </div>
                <div>
                  <p className="contact-label">{t.contact.india.title}</p>
                  <p className="contact-address">{t.contact.india.address}</p>
                </div>
              </div>

              {/* Germany Office */}
              <div className="contact-item" data-testid="office-card-germany">
                <div className="contact-icon-wrapper" style={{ backgroundColor: '#0A254010' }}>
                  <MapPin size={24} color="#0A2540" />
                </div>
                <div>
                  <p className="contact-label">{t.contact.germany.title}</p>
                  <p className="contact-address">{t.contact.germany.address}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="map-container" data-testid="map-container">
            <MapContainer
              center={[30, 40]}
              zoom={3}
              style={{ height: '100%', width: '100%', borderRadius: '12px' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              {/* Pune, India Marker */}
              <Marker position={[18.5204, 73.8567]}>
                <Popup>
                  <strong>{t.contact.india.title}</strong><br />
                  {t.contact.india.address}
                </Popup>
              </Marker>
              {/* Moelln, Germany Marker */}
              <Marker position={[53.6293, 10.6897]}>
                <Popup>
                  <strong>{t.contact.germany.title}</strong><br />
                  {t.contact.germany.address}
                </Popup>
              </Marker>
            </MapContainer>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-col items-center md:items-start">
              <p className="footer-text" data-testid="footer-copyright">{t.footer.copyright}</p>
              <p className="footer-tagline" data-testid="footer-tagline">{t.footer.tagline}</p>
            </div>
            
            <div className="flex flex-col items-center md:items-end">
              <p className="footer-contact-title" data-testid="footer-contact-title">{t.footer.contact}</p>
              <div className="flex items-center gap-4 mt-3">
                <a 
                  href="https://linkedin.com/company/elixir-ai-pvt-ltd" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link"
                  data-testid="footer-linkedin-link"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={20} />
                </a>
                <a 
                  href="mailto:mayurakshi@elixirai.co.in"
                  className="social-link"
                  data-testid="footer-email-link"
                  aria-label="Email"
                >
                  <Mail size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;